import { parseVtt, type Cue } from "./matching/pipeline";

// A meeting's recording laid out over the whole meeting.
//
// Stopping and restarting a recording makes Zoom deliver one set of files per
// part, and each part's video and transcript restart at 00:00:00 (meeting 406,
// three parts, LCATOMICZO-70). Recording also often starts late or stops early:
// meeting 206 was recorded from 14 minutes in. So the player covers the meeting
// from its start to its end, places each recorded part where it happened, and
// plays black wherever nothing was recorded (Sam, 2026-09-16).
//
// Two times, kept apart on purpose:
//
// - Recording time: seconds since the first recording started. It is what is
//   stored, in `offset_seconds`, in matcher output and in transcript-search `t`,
//   and it is unchanged by any of this, so no stored value had to move.
// - Meeting time: seconds since the meeting started. It is what the player, the
//   progress bar, the badges and the transcript show.
//
// They differ by `leadIn`, how long after the meeting started recording did.
// A meeting recorded from its first second has leadIn 0 and the two are equal.
//
// Offsets come from each row's own recording_start, not from its part's, so a
// transcript that starts a moment after its video still lines up.

type RecordingLike = {
  id: number | string;
  recording_type: string;
  file_type: string;
  recording_start?: string | null;
  recording_end?: string | null;
  transcript_content?: string | null;
};

type MeetingTimes = {
  meeting_start_time?: string | null;
  meeting_end_time?: string | null;
};

export type RecordingPart<T extends RecordingLike = RecordingLike> = {
  index: number;
  /** Seconds after the meeting started (after the first recording, in recordingParts). */
  offset: number;
  /** Seconds, or null when no row of the part has an end time. */
  duration: number | null;
  /** When the part started, as Zoom reported it. */
  startedAt: string | null;
  rows: T[];
  /** The part's transcript: its audio transcript, else its captions. */
  transcript: T | null;
};

export type MeetingSpan<T extends RecordingLike = RecordingLike> = {
  /** Seconds from the meeting's start to its end. */
  duration: number;
  /** Seconds between the meeting starting and the first recording starting. */
  leadIn: number;
  /** Recorded parts, offsets in meeting time. */
  parts: RecordingPart<T>[];
};

export type TimelineCue = Cue & { part: number };

/** Where a meeting time falls: inside a recorded part, or in black. */
export type SpanPosition =
  | { kind: "part"; part: number; local: number }
  | { kind: "gap"; next: number | null };

/** What the player puts on `videoRef`: one clock over the whole meeting. */
export interface MeetingClock {
  /** Meeting time in seconds. Setting it moves to whatever is at that time. */
  currentTime: number;
  readonly paused: boolean;
  readonly readyState: number;
  play(): Promise<void>;
  pause(): void;
  addEventListener(type: "timeupdate", listener: () => void): void;
  removeEventListener(type: "timeupdate", listener: () => void): void;
}

const TRANSCRIPT_TYPES = ["audio_transcript", "closed_caption"];

function ms(value: string | null | undefined): number | null {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : t;
}

/**
 * The origin of recording time: the earliest recording_start across every row,
 * media and transcripts alike, so the player and the transcript agree even when
 * the first part has no transcript (406's 13-second part 1 has none).
 */
function recordingOriginMs(rows: RecordingLike[]): number | null {
  let origin: number | null = null;
  for (const r of rows) {
    const t = ms(r.recording_start);
    if (t != null && (origin == null || t < origin)) origin = t;
  }
  return origin;
}

function recordingEndMs(rows: RecordingLike[]): number | null {
  let end: number | null = null;
  for (const r of rows) {
    const t = ms(r.recording_end);
    if (t != null && (end == null || t > end)) end = t;
  }
  return end;
}

/** Seconds between the first recording starting and this row starting. */
export function rowOffset(row: RecordingLike, rows: RecordingLike[]): number {
  const origin = recordingOriginMs(rows);
  const t = ms(row.recording_start);
  return origin == null || t == null ? 0 : (t - origin) / 1000;
}

/**
 * Group recording rows into parts, in order, with offsets in recording time.
 * Rows of one part share a start to the second; a row without a start joins
 * the first part.
 */
export function recordingParts<T extends RecordingLike>(
  recordings: T[] | undefined,
): RecordingPart<T>[] {
  const rows = recordings ?? [];
  if (rows.length === 0) return [];
  const origin = recordingOriginMs(rows);

  const byStart = new Map<number, T[]>();
  for (const r of rows) {
    const t = ms(r.recording_start);
    const key = t == null ? (origin ?? 0) : Math.floor(t / 1000) * 1000;
    const group = byStart.get(key);
    if (group) group.push(r);
    else byStart.set(key, [r]);
  }

  return [...byStart.entries()]
    .sort(([a], [b]) => a - b)
    .map(([key, partRows], index) => {
      let duration: number | null = null;
      for (const r of partRows) {
        const start = ms(r.recording_start);
        const end = ms(r.recording_end);
        if (start != null && end != null) {
          duration = Math.max(duration ?? 0, (end - start) / 1000);
        }
      }
      const withText = (type: string) =>
        partRows.find((r) => r.recording_type === type && r.transcript_content);
      return {
        index,
        offset: origin == null ? 0 : (key - origin) / 1000,
        duration,
        startedAt:
          partRows.find((r) => r.recording_start)?.recording_start ?? null,
        rows: partRows,
        transcript:
          withText(TRANSCRIPT_TYPES[0]) ??
          withText(TRANSCRIPT_TYPES[1]) ??
          null,
      };
    });
}

/**
 * The meeting from start to end with its recorded parts placed in it.
 *
 * A meeting with no end time (Zoom's meeting.ended does not always arrive)
 * ends when its last recording does. The span always contains every
 * recording: one stamped before the meeting start moves the start back, one
 * running past the end moves the end out. Without a meeting row it is just the
 * recording, with leadIn 0.
 */
export function meetingSpan<T extends RecordingLike>(
  meeting: MeetingTimes | null | undefined,
  recordings: T[] | undefined,
): MeetingSpan<T> {
  const rows = recordings ?? [];
  const recStart = recordingOriginMs(rows);
  const recEnd = recordingEndMs(rows);

  let start = ms(meeting?.meeting_start_time) ?? recStart;
  if (recStart != null && (start == null || recStart < start)) start = recStart;
  let end = ms(meeting?.meeting_end_time) ?? recEnd ?? start;
  if (recEnd != null && (end == null || recEnd > end)) end = recEnd;

  const leadIn =
    start == null || recStart == null ? 0 : (recStart - start) / 1000;
  const parts = recordingParts(rows).map((p) => ({
    ...p,
    offset: p.offset + leadIn,
  }));
  // A last part with no end time still has to fit.
  const lastPart = parts[parts.length - 1];
  const spanFromTimes = start == null || end == null ? 0 : (end - start) / 1000;
  const duration = Math.max(
    spanFromTimes,
    lastPart ? lastPart.offset + (lastPart.duration ?? 0) : 0,
  );
  return { duration, leadIn, parts };
}

/**
 * Seconds actually recorded: the sum of the parts, so a gap between parts
 * counts as unrecorded. Null when a part has no end time, since the sum would
 * then understate what was recorded.
 */
export function recordedSeconds(span: MeetingSpan): number | null {
  let total = 0;
  for (const p of span.parts) {
    if (p.duration == null) return null;
    total += p.duration;
  }
  return total;
}

/** Below this much unrecorded time the meeting counts as fully recorded. */
export const COVERAGE_THRESHOLD_SECONDS = 120;

/**
 * Seconds of the meeting that were not recorded, or null when there is
 * nothing worth saying: no recordings, a part that cannot be measured, or a
 * shortfall under the threshold. A pause that did not split the recording into
 * parts is invisible here, so it is never counted.
 */
export function coverageShortfall(span: MeetingSpan): number | null {
  if (span.parts.length === 0) return null;
  const recorded = recordedSeconds(span);
  if (recorded == null) return null;
  const shortfall = span.duration - recorded;
  return shortfall < COVERAGE_THRESHOLD_SECONDS ? null : shortfall;
}

export const toMeetingTime = (recordingTime: number, leadIn: number) =>
  recordingTime + leadIn;

export const toRecordingTime = (meetingTime: number, leadIn: number) =>
  Math.max(0, meetingTime - leadIn);

/**
 * Every part's transcript cues, in order. `shift` is added to recording time:
 * 0 for the matcher, which works in recording time, and the span's leadIn for
 * anything shown on the meeting clock.
 */
export function mergedCues(parts: RecordingPart[], shift = 0): TimelineCue[] {
  const allRows = parts.flatMap((p) => p.rows);
  return parts.flatMap((p) => {
    if (!p.transcript?.transcript_content) return [];
    const at = rowOffset(p.transcript, allRows) + shift;
    return parseVtt(p.transcript.transcript_content).map((c) => ({
      start: c.start + at,
      end: c.end + at,
      text: c.text,
      part: p.index,
    }));
  });
}

/**
 * What is at a meeting time: a part and the time inside it, or black and the
 * part that comes next (null after the last one). A part with no end time is
 * taken to run until the next one starts.
 */
export function positionAt(parts: RecordingPart[], t: number): SpanPosition {
  for (const p of parts) {
    if (t < p.offset) return { kind: "gap", next: p.index };
    const end =
      p.duration != null
        ? p.offset + p.duration
        : (parts[p.index + 1]?.offset ?? Infinity);
    if (t < end) return { kind: "part", part: p.index, local: t - p.offset };
  }
  return { kind: "gap", next: null };
}

function vttTime(seconds: number): string {
  const total = Math.max(0, Math.round(seconds * 1000));
  const h = Math.floor(total / 3_600_000);
  const m = Math.floor(total / 60_000) % 60;
  const s = Math.floor(total / 1000) % 60;
  const milli = total % 1000;
  const pad = (n: number, w = 2) => String(n).padStart(w, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}.${pad(milli, 3)}`;
}

/**
 * Cues as WebVTT, for match_timestamps, which returns offsets in the time of the
 * VTT it is given. Fed recording-time cues, its offsets are stored as they are.
 */
export function mergedVtt(cues: Cue[]): string {
  return (
    "WEBVTT\n\n" +
    cues
      .map((c) => `${vttTime(c.start)} --> ${vttTime(c.end)}\n${c.text}`)
      .join("\n\n") +
    "\n"
  );
}
